import Phaser from 'phaser'
import Player from '../characters/player'
import PlatformBuildController from '../classes/PlatformBuildController'
import ScrapDisplay from '../classes/ScrapDisplay'
import SettingsMenu from '../classes/SettingsMenu'

export default class Tutorial4 extends Phaser.Scene {
  private cursors!: Phaser.Types.Input.Keyboard.CursorKeys
  private camera!: Phaser.Cameras.Scene2D.Camera
  private player!: Player
  private groundLayer!: Phaser.Tilemaps.TilemapLayer
  private spikeGroup!: Phaser.Physics.Arcade.StaticGroup
  private isPlayerDead!: boolean
  private resetKey!: Phaser.Input.Keyboard.Key
  private endPoint!: Phaser.Types.Tilemaps.TiledObject
  private platformBuilder!: PlatformBuildController
  private scrapDisplay!: ScrapDisplay
  private settingsMenu!: SettingsMenu

  constructor() {
    super('tutorial_4')
  }

  preload() {
    if (!this.input.keyboard) {
      throw new Error('Keyboard input is not available in this scene')
    }
    this.cursors = this.input.keyboard.createCursorKeys()
    this.resetKey = this.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.R)
  }

  create() {
    this.isPlayerDead = false

    // Load and setup the map
    const map = this.make.tilemap({ key: 'tutorial_map_4' })
    const tiles = map.addTilesetImage('0x72-industrial-tileset-32px-extruded', 'tiles')
    if (!tiles) {
      throw new Error('Missing tileset: 0x72-industrial-tileset-32px-extruded')
    }

    map.createLayer('Background', tiles)
    const groundLayer = map.createLayer('Ground', tiles)
    if (!groundLayer) {
      throw new Error('Missing Ground layer in tutorial_map_4')
    }
    this.groundLayer = groundLayer
    map.createLayer('Foreground', tiles)

    // Setup spawn and end points
    const spawnPoint = map.findObject('Objects', (obj) => obj.name === 'Spawn Point')
    if (!spawnPoint || typeof spawnPoint.x !== 'number' || typeof spawnPoint.y !== 'number') {
      throw new Error('Spawn Point object is missing or invalid in tutorial_map_4')
    }

    const endPoint = map.findObject('Objects', (obj) => obj.name === 'End Point')
    if (!endPoint || typeof endPoint.x !== 'number' || typeof endPoint.y !== 'number') {
      throw new Error('End Point object is missing or invalid in tutorial_map_4')
    }
    this.endPoint = endPoint

    // Create player
    this.player = new Player(this, spawnPoint.x, spawnPoint.y)
    const playerSprite = this.player.getSprite()

    // Setup collisions
    this.groundLayer.setCollisionByProperty({ collides: true })
    this.physics.world.addCollider(playerSprite, this.groundLayer)

    // Handle spike tiles
    this.spikeGroup = this.physics.add.staticGroup()
    this.groundLayer.forEachTile((tile) => {
      if (tile.index === 77) {
        const spike = this.spikeGroup.create(tile.getCenterX(), tile.getCenterY(), 'spike')

        spike.rotation = tile.rotation
        if (spike.angle === 0) spike.body.setSize(32, 6).setOffset(0, 26)
        else if (spike.angle === -90) spike.body.setSize(6, 32).setOffset(26, 0)
        else if (spike.angle === 90) spike.body.setSize(6, 32).setOffset(0, 0)

        this.groundLayer.removeTileAt(tile.x, tile.y)
      }
    })

    // Setup scrap display with 5 starting scrap
    this.scrapDisplay = new ScrapDisplay(this, 0)

    // Setup platform builder with scrap system
    this.platformBuilder = new PlatformBuildController(this, map, groundLayer, {
      buildEnabled: true,
      removeEnabled: true,
      buildTileIndex: 353,
      onlyRemovePlayerMade: true,
      showMarker: true,
      scrapDisplay: this.scrapDisplay,
      buildCost: 1,
      removeReward: 1,
    })

    const platLocations = [
      [5, 4], [6, 4],[6, 5], [6, 6], [7, 6],
    ]

    for (const [tileX, tileY] of platLocations) {
      this.platformBuilder.createPlayerMadeTileAt(tileX * 32, tileY * 32)
    }

    // On-screen instructions
    this.add
      .text(16, 16, 'Use SCRAP to build platforms!', {
        font: '18px monospace',
        color: '#000000',
        padding: { x: 20, y: 10 },
        backgroundColor: '#ffffff',
      })
      .setScrollFactor(0)

    this.settingsMenu = new SettingsMenu(this, { pauseGameplay: true, openButtonY: 56 })

    // Camera setup
    this.initCamera(map)
  }

  private initCamera(map: Phaser.Tilemaps.Tilemap): void {
    this.camera = this.cameras.main
    this.cameras.main.startFollow(this.player.getSprite())
    this.camera.setBounds(0, 0, map.widthInPixels, map.heightInPixels)
  }

  update(): void {
    if (this.isPlayerDead) return
    if (this.settingsMenu.isOpen()) return

    this.player.update()
    this.platformBuilder.update()

    // Check for death
    if (
      Phaser.Input.Keyboard.JustDown(this.resetKey) ||
      this.player.getSprite().y > this.groundLayer.height ||
      this.physics.world.overlap(this.player.getSprite(), this.spikeGroup)
    ) {
      this.isPlayerDead = true
      this.camera.shake(100, 0.05)
      this.camera.fade(250, 0, 0, 0)
      this.player.freeze()

      this.camera.once('camerafadeoutcomplete', () => {
        this.player.destroy()
        this.scrapDisplay.destroy()
        this.platformBuilder.destroy()
        this.scene.restart()
      })
    }

    // Check for level completion
    if (
      Math.abs(this.player.getSprite().y - Math.round(this.endPoint.y ?? 0)) <= 10 &&
      Math.abs(this.player.getSprite().x - Math.round(this.endPoint.x ?? 0)) <= 10
    ) {
      this.scene.start('startScreen')
    }
  }
}
